'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { GroupHeaderCell } from '@terra/ui-design-system';
import { GRAPH_NODE_ICON } from '@terra/ui-graphs/internal-network';
import { ListPageCellFrame } from '../../../common/ListPageCellFrame';
import { rowClickBlockerProps } from '../../rowClickBlocker';
import {
  InternalAdEntityKindBadge,
  InternalAssetDateCell,
  InternalAssetFlagCell,
  InternalAssetNameCell,
  InternalAssetTextCell,
  InternalCanonicalAssetKindBadge,
  InternalCanonicalAssetStatusCell,
} from './InternalAssetsTableCells';
import {
  isInternalAssetsGroupHeaderRow,
  isInternalAssetsSortColumnForMode,
  type InternalAdAssetGridItem,
  type InternalAssetGridItem,
  type InternalAssetsListMode,
  type InternalAssetsTableRow,
  type InternalCanonicalAssetGridItem,
} from './internal-assets-table.model';

const NAME_COLUMN_MIN_WIDTH_PX = 240;
const NAME_COLUMN_ID = 'displayName';

const preserveServerSortOrder = () => 0;

type InternalAssetValueColumn<Item> = {
  colId: string;
  headerName: string;
  width: number;
  render: (item: Item) => ReactNode;
};

export function useInternalAssetsTableColumnDefs({
  mode,
  sortApplied,
  collapsedGroupKeys,
  onToggleGroup,
}: {
  mode: InternalAssetsListMode;
  sortApplied: boolean;
  collapsedGroupKeys: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
}): ColDef<InternalAssetsTableRow>[] {
  const tTable = useTranslations('proxy.assets.table');
  const tAssetDetail = useTranslations('externalNetwork.assetDetail');
  const tAssetList = useTranslations('internalNetwork.assetList');
  const tInternalNetwork = useTranslations('internalNetwork');
  const tEnrollmentColumns = useTranslations(
    'internalNetwork.enrollments.columns',
  );

  return useMemo(() => {
    const isSortable = (colId: string) =>
      sortApplied && isInternalAssetsSortColumnForMode({ mode, colId });

    const adColumns: InternalAssetValueColumn<InternalAdAssetGridItem>[] = [
      {
        colId: 'isCrownJewel',
        headerName: tAssetDetail('crownJewel'),
        width: 148,
        render: (item) => (
          <InternalAssetFlagCell
            isSet={item.entity.isCrownJewel}
            label={tInternalNetwork('scenarios.crownJewel')}
            colorPalette="orange"
          />
        ),
      },
      {
        colId: 'isOwned',
        headerName: tAssetDetail('ownedPrincipal'),
        width: 148,
        render: (item) => (
          <InternalAssetFlagCell
            isSet={item.entity.isOwned}
            label={tInternalNetwork('ownedPrincipals.title')}
            colorPalette="blue"
          />
        ),
      },
      {
        colId: 'samAccountName',
        headerName: tAssetList('accountName'),
        width: 200,
        render: (item) => (
          <InternalAssetTextCell value={item.entity.samAccountName} />
        ),
      },
      {
        colId: 'entityKind',
        headerName: tAssetDetail('type'),
        width: 184,
        render: (item) => (
          <InternalAdEntityKindBadge entityKind={item.entity.entityKind} />
        ),
      },
      {
        colId: 'firstObservedAt',
        headerName: tAssetDetail('firstSeen'),
        width: 168,
        render: (item) => (
          <InternalAssetDateCell value={item.entity.firstObservedAt ?? null} />
        ),
      },
    ];

    const canonicalColumns: InternalAssetValueColumn<InternalCanonicalAssetGridItem>[] =
      [
        {
          colId: 'assetKind',
          headerName: tAssetDetail('type'),
          width: 168,
          render: (item) => (
            <InternalCanonicalAssetKindBadge asset={item.asset} />
          ),
        },
        {
          colId: 'presenceStatus',
          headerName: tEnrollmentColumns('status'),
          width: 184,
          render: (item) => (
            <InternalCanonicalAssetStatusCell asset={item.asset} />
          ),
        },
        {
          colId: 'primaryAddress',
          headerName: tAssetDetail('ipAddress'),
          width: 168,
          render: (item) => (
            <InternalAssetTextCell value={item.asset.primaryAddress} />
          ),
        },
        {
          colId: 'primaryHostname',
          headerName: tAssetDetail('hostname'),
          width: 200,
          render: (item) => (
            <InternalAssetTextCell value={item.asset.primaryHostname} />
          ),
        },
        {
          colId: 'lastObservedAt',
          headerName: tAssetDetail('lastSeen'),
          width: 168,
          render: (item) => (
            <InternalAssetDateCell value={item.asset.lastObservedAt} />
          ),
        },
      ];

    const renderValueCell = ({
      item,
      colId,
    }: {
      item: InternalAssetGridItem;
      colId: string;
    }): ReactNode => {
      if (item.source === 'ad') {
        return adColumns.find((column) => column.colId === colId)?.render(item);
      }

      return canonicalColumns
        .find((column) => column.colId === colId)
        ?.render(item);
    };

    const activeColumns: readonly Omit<
      InternalAssetValueColumn<InternalAssetGridItem>,
      'render'
    >[] = mode === 'ad' ? adColumns : canonicalColumns;
    const valueColumns = activeColumns.map(
      ({ colId, headerName, width }): ColDef<InternalAssetsTableRow> => ({
        colId,
        headerName,
        width,
        minWidth: width,
        sortable: isSortable(colId),
        comparator: preserveServerSortOrder,
        cellRenderer: (params: ICellRendererParams<InternalAssetsTableRow>) => {
          const item = params.data;

          return item && !isInternalAssetsGroupHeaderRow(item) ? (
            <ListPageCellFrame>
              {renderValueCell({ item, colId })}
            </ListPageCellFrame>
          ) : null;
        },
      }),
    );

    const nameColumn: ColDef<InternalAssetsTableRow> = {
      colId: NAME_COLUMN_ID,
      headerName: tTable('name'),
      flex: 1,
      minWidth: NAME_COLUMN_MIN_WIDTH_PX,
      lockPosition: 'left',
      sortable: isSortable(NAME_COLUMN_ID),
      comparator: preserveServerSortOrder,
      colSpan: (params) =>
        isInternalAssetsGroupHeaderRow(params.data)
          ? valueColumns.length + 1
          : 1,
      cellRenderer: (params: ICellRendererParams<InternalAssetsTableRow>) => {
        if (isInternalAssetsGroupHeaderRow(params.data)) {
          const GroupIcon = params.data.entityKind
            ? GRAPH_NODE_ICON[params.data.entityKind]
            : null;

          return (
            <GroupHeaderCell
              group={params.data}
              isCollapsed={collapsedGroupKeys.has(params.data.groupKey)}
              onToggle={onToggleGroup}
              containerProps={rowClickBlockerProps}
              icon={GroupIcon ? <GroupIcon size={16} /> : undefined}
            />
          );
        }

        return params.data ? (
          <InternalAssetNameCell item={params.data} />
        ) : null;
      },
    };

    return [nameColumn, ...valueColumns];
  }, [
    collapsedGroupKeys,
    mode,
    onToggleGroup,
    sortApplied,
    tAssetDetail,
    tAssetList,
    tEnrollmentColumns,
    tInternalNetwork,
    tTable,
  ]);
}
